
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { TeacherCardActions } from './TeacherCardActions';
import { Mail, Phone, Briefcase } from 'lucide-react';

interface TeacherCardProps {
  teacher: {
    id: string;
    name: string;
    email: string;
    phone: string;
    profileImage?: string;
    qualifications?: string[];
    department?: string;
    bio?: string;
  };
  onDeleteSuccess?: () => void;
}

const TeacherCardV2: React.FC<TeacherCardProps> = ({ teacher, onDeleteSuccess }) => {
  const { id, name, email, phone, profileImage, department, bio, qualifications } = teacher;
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Use a placeholder bio if none exists
  const teacherBio = bio || "Dedicated educator fostering knowledge and growth in students through innovative teaching methods.";
  
  // Get first qualification if available
  const primaryQualification = qualifications && qualifications.length > 0 
    ? qualifications[0] 
    : "Teacher";

  // Get initials for fallback
  const nameInitials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  console.log(`🖼️ TeacherCard: Rendering teacher ${name} with profileImage:`, profileImage?.substring(0, 50) + '...');

  // Enhanced image validation - check if we have a valid profile image
  const hasValidProfileImage = profileImage && 
    profileImage.trim() !== '' && 
    !imageError && 
    (profileImage.startsWith('data:image/') || 
     profileImage.startsWith('http') || 
     profileImage.startsWith('https://') || 
     profileImage.startsWith('/') ||
     profileImage.startsWith('blob:'));

  console.log(`🔍 Teacher ${name} image validation:`, {
    hasImage: !!profileImage,
    imageLength: profileImage?.length || 0,
    isBase64: profileImage?.startsWith('data:'),
    hasValidProfileImage,
    imageError,
    imageLoaded
  });

  // Enhanced 3D effect with mouse tracking
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHovering) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1)
    const normalizedX = x / (rect.width / 2);
    const normalizedY = y / (rect.height / 2);
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };

  const handleImageLoad = () => {
    console.log(`✅ Image loaded successfully for teacher ${name}`);
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log(`❌ Image load error for teacher ${name}, using fallback`);
    setImageError(true);
    setImageLoaded(false);
  };

  return (
    <Link to={`/teachers/${id}`} className="block">
      <Card 
        className="flex overflow-hidden bg-black text-white rounded-xl shadow-lg border-none card-3d-container"
        style={{
          transform: isHovering ? 
            `perspective(1000px) rotateY(${mousePosition.x * 10}deg) rotateX(${-mousePosition.y * 10}deg) scale(1.03)` : 
            'perspective(1000px) rotateY(0) rotateX(0) scale(1)',
          boxShadow: isHovering ? 
            '0 30px 60px rgba(0, 0, 0, 0.2), 0 0 40px rgba(0, 0, 0, 0.2)' : 
            '0 5px 15px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePosition({ x: 0, y: 0 });
        }}
      >
        {/* Profile Image Section */}
        <div className="relative w-2/5 overflow-hidden rounded-l-xl flex items-center justify-center">
          {hasValidProfileImage && !imageError ? (
            <div className="h-full w-full relative">
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-300 flex items-center justify-center">
                  <div className="text-white text-4xl font-bold">
                    {nameInitials}
                  </div>
                </div>
              )}
              <img 
                src={profileImage} 
                alt={`${name} profile`}
                className={`h-full w-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-orange-500 to-amber-300 flex items-center justify-center">
              <div className="text-white text-4xl font-bold">
                {nameInitials}
              </div>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="w-3/5 p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-xl font-bold text-white tracking-wide">{name}</h3>
            <p className="text-gray-400 mb-3">{primaryQualification}</p>
            
            <p className="text-sm text-gray-400 line-clamp-2 mb-4">
              {teacherBio}
            </p>
          </div>
          
          <div className="flex space-x-2 mt-auto">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors">
              <Mail size={18} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors">
              <Phone size={18} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors">
              <Briefcase size={18} className="text-white" />
            </div>
            
            {/* Original card actions (hidden) but preserved for functionality */}
            <div className="hidden">
              <TeacherCardActions 
                teacherId={id} 
                teacherName={name} 
                onDeleteSuccess={onDeleteSuccess} 
              />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default TeacherCardV2;
